/**
 * AppointmentsCalendarView - Calendar vizual pentru programări
 * Design compact consistent cu restul aplicației
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, isSameDay, isSameMonth } from 'date-fns';
import { ro } from 'date-fns/locale';

import {
  AppointmentListItem,
  APPOINTMENT_STATUS_CONFIG,
} from '../../types/appointments';
import { getAppointments } from '../../services/appointmentsApi';

interface AppointmentsCalendarViewProps {
  deviceId?: string;
  onAppointmentClick?: (appointment: AppointmentListItem) => void;
  onCreateClick?: (date: Date, time?: string) => void;
}

type ViewType = 'week' | 'month';

const AppointmentsCalendarView: React.FC<AppointmentsCalendarViewProps> = ({
  deviceId,
  onAppointmentClick,
  onCreateClick,
}) => {
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewType, setViewType] = useState<ViewType>('week');

  // Calculate date range
  const getDateRange = useCallback(() => {
    if (viewType === 'week') {
      return {
        start: startOfWeek(currentDate, { weekStartsOn: 1 }),
        end: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    } else {
      return {
        start: startOfMonth(currentDate),
        end: endOfMonth(currentDate),
      };
    }
  }, [currentDate, viewType]);

  // Load appointments
  const loadAppointments = useCallback(async () => {
    setLoading(true);
    const { start, end } = getDateRange();
    
    try {
      const response = await getAppointments({
        device_id: deviceId,
        date_from: format(start, 'yyyy-MM-dd'),
        date_to: format(end, 'yyyy-MM-dd'),
        limit: 500,
      });
      setAppointments(response.items);
    } catch (error) {
      console.error('Error loading appointments:', error);
    } finally {
      setLoading(false);
    }
  }, [deviceId, getDateRange]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Navigation
  const navigatePrev = () => {
    setCurrentDate(prev => viewType === 'week' ? addWeeks(prev, -1) : addMonths(prev, -1));
  };

  const navigateNext = () => {
    setCurrentDate(prev => viewType === 'week' ? addWeeks(prev, 1) : addMonths(prev, 1));
  };

  const navigateToday = () => {
    setCurrentDate(new Date());
  };

  // Get appointments for a specific date
  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter(apt => 
      isSameDay(new Date(apt.scheduled_date), date)
    ).sort((a, b) => a.scheduled_start_time.localeCompare(b.scheduled_start_time));
  };

  // Generate calendar days
  const generateDays = () => {
    const { start, end } = getDateRange();
    const days: Date[] = [];
    let current = start;
    
    while (current <= end) {
      days.push(current);
      current = addDays(current, 1);
    }
    
    return days;
  };

  const days = generateDays();
  const today = new Date();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={navigatePrev}>
            <PrevIcon />
          </IconButton>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, minWidth: 180, textAlign: 'center' }}>
            {viewType === 'week' 
              ? `${format(days[0] || new Date(), 'd MMM', { locale: ro })} - ${format(days[days.length - 1] || new Date(), 'd MMM yyyy', { locale: ro })}`
              : format(currentDate, 'MMMM yyyy', { locale: ro })
            }
          </Typography>
          <IconButton size="small" onClick={navigateNext}>
            <NextIcon />
          </IconButton>
          <Tooltip title="Azi">
            <IconButton size="small" onClick={navigateToday}>
              <TodayIcon sx={{ fontSize: '1.1rem' }} />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {loading && <CircularProgress size={20} />}
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={(_, v) => v && setViewType(v)}
            size="small"
          >
            <ToggleButton value="week" sx={{ py: 0.3, px: 1.5, fontSize: '0.75rem' }}>
              Săptămână
            </ToggleButton>
            <ToggleButton value="month" sx={{ py: 0.3, px: 1.5, fontSize: '0.75rem' }}>
              Lună
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: viewType === 'week' ? 'repeat(7, 1fr)' : 'repeat(7, 1fr)',
          gap: 0.5,
          bgcolor: 'grey.100',
          p: 0.5,
          borderRadius: 1,
        }}
      >
        {/* Day headers */}
        {['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'].map((day, idx) => (
          <Box
            key={day}
            sx={{
              textAlign: 'center',
              py: 0.5,
              bgcolor: 'grey.800',
              color: 'common.white',
              fontSize: '0.7rem',
              fontWeight: 600,
              borderRadius: 0.5,
            }}
          >
            {day}
          </Box>
        ))}

        {/* Calendar cells */}
        {days.map((day, idx) => {
          const dayAppointments = getAppointmentsForDate(day);
          const isToday = isSameDay(day, today);
          const isCurrentMonth = isSameMonth(day, currentDate);
          
          return (
            <Box
              key={idx}
              onClick={() => onCreateClick?.(day)}
              sx={{
                minHeight: viewType === 'week' ? 120 : 80,
                bgcolor: isToday ? 'primary.50' : 'background.paper',
                borderRadius: 0.5,
                p: 0.5,
                cursor: 'pointer',
                opacity: isCurrentMonth || viewType === 'week' ? 1 : 0.5,
                border: isToday ? '2px solid' : '1px solid',
                borderColor: isToday ? 'primary.main' : 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
            >
              {/* Day number */}
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  fontWeight: isToday ? 700 : 500,
                  color: isToday ? 'primary.main' : 'text.primary',
                  mb: 0.3,
                }}
              >
                {format(day, 'd')}
              </Typography>

              {/* Appointments */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                {dayAppointments.slice(0, viewType === 'week' ? 5 : 3).map((apt) => (
                  <Tooltip
                    key={apt.id}
                    title={`${apt.scheduled_start_time.slice(0, 5)} - ${apt.patient_name} - ${apt.investigation_name}`}
                  >
                    <Chip
                      label={`${apt.scheduled_start_time.slice(0, 5)} ${apt.patient_name.split(' ')[0]}`}
                      size="small"
                      color={APPOINTMENT_STATUS_CONFIG[apt.status]?.color || 'default'}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAppointmentClick?.(apt);
                      }}
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          px: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  </Tooltip>
                ))}
                {dayAppointments.length > (viewType === 'week' ? 5 : 3) && (
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', pl: 0.5 }}>
                    +{dayAppointments.length - (viewType === 'week' ? 5 : 3)} mai multe
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
        {Object.entries(APPOINTMENT_STATUS_CONFIG).map(([key, cfg]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: `${cfg.color}.main`,
              }}
            />
            <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
              {cfg.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AppointmentsCalendarView;
